import { motion } from "framer-motion";
import {
    Wallet,
    LineChart,
    TrendingUp,
    Banknote,
    Compass,
    ArrowRight,
} from "lucide-react";
import { useHomeIQ } from "@/homeiq/state/HomeIQContext";
import { fmtINR, fmtINRCompact } from "@/homeiq/services/format";

// Static supporting-cards content. These are marketing statements about the
// product ("what HomeIQ covers"), not personalised financial numbers, so per
// the architectural rule they remain hardcoded strings.
const intelligences = [
    {
        id: "market",
        label: "Where should I buy?",
        title: "Find the right neighbourhood",
        body: "Compare price, growth and lifestyle across 24 verified Bengaluru micro-markets.",
        icon: LineChart,
        stat: { v: "24", l: "Verified Bengaluru markets" },
        chips: ["HSR Layout", "Sarjapur Road", "Whitefield"],
    },
    {
        id: "investment",
        label: "Is it worth it?",
        title: "See if buying beats renting",
        body: "5- and 10-year wealth build vs. continuing to rent.",
        icon: TrendingUp,
        stat: { v: "Live", l: "Wealth projection · personalised" },
    },
    {
        id: "loan",
        label: "Will the EMI fit?",
        title: "Build an EMI that fits",
        body: "Compare 6 lenders, simulate scenarios, find the month it beats rent.",
        icon: Banknote,
        stat: { v: "Live", l: "Bank rates · Feb 2026" },
    },
    {
        id: "vastu",
        label: "Does it feel right?",
        title: "A final check on your dream home",
        body: "Optional, tradition-aware audit on the home you've shortlisted.",
        icon: Compass,
        stat: { v: "Advisory", l: "Independent of HomeIQ Score" },
    },
];

export default function Features() {
    const { setActiveSection, rent, landingAffordabilityOutputs } = useHomeIQ();
    // Rent-gate personalised numbers per product decision (Option C).
    // When rent is entered, values come from the Landing Reference Profile
    // pipeline (rent-inferred inputs → Core engines) so the affordability
    // tiles genuinely respond to the slider. See landing-profile.md.
    const hasRent = rent != null && rent > 0;
    const rentDisplay = hasRent ? `₹${rent.toLocaleString("en-IN")}` : "—";
    const homeDisplay = hasRent ? fmtINR(landingAffordabilityOutputs.max_affordable_property) : "—";
    const emiDisplay = hasRent ? fmtINRCompact(landingAffordabilityOutputs.emi_on_eligible_loan) : "—";
    return (
        <section
            id="features"
            data-testid="features-section"
            className="relative py-16 md:py-24 px-6 md:px-12 bg-[#F7F8FB]"
        >
            <div className="max-w-7xl mx-auto">
                <div className="mb-12 md:mb-16 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
                    <div>
                        <p className="text-[11px] tracking-[0.3em] uppercase text-[#1E6BFF] mb-4 font-semibold">
                            — How HomeIQ helps
                        </p>
                        <h2
                            data-testid="features-heading"
                            className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tight max-w-2xl leading-[1.02] text-[#0A1F44]"
                        >
                            Five questions.
                            <br />
                            <span className="text-[#0A1F44]/45">
                                One confident answer.
                            </span>
                        </h2>
                    </div>
                    <p className="text-[#0A1F44]/65 max-w-sm text-base sm:text-lg leading-relaxed">
                        From your first "can I really afford this?" to the day
                        you get the keys.
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                    {/* AFFORDABILITY — accent, dense with sample data */}
                    <motion.button
                        type="button"
                        onClick={() => setActiveSection("affordability")}
                        data-testid="intelligence-card-affordability"
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{
                            duration: 0.7,
                            ease: [0.16, 1, 0.3, 1],
                        }}
                        className="text-left md:col-span-8 md:row-span-2 group relative overflow-hidden rounded-3xl border bg-[#0A1F44] border-[#0A1F44] text-white p-7 md:p-8 hover:bg-[#0E2654] transition-all duration-500 cursor-pointer"
                    >
                        <div className="absolute inset-0 blueprint-grid opacity-20 pointer-events-none" />

                        <div className="relative z-10 flex items-start justify-between mb-6">
                            <span className="text-[11px] tracking-[0.25em] uppercase font-semibold text-[#7FA8FF]">
                                01 — Can I afford it?
                            </span>
                            <Wallet
                                className="w-5 h-5 text-white"
                                strokeWidth={1.75}
                            />
                        </div>

                        <div className="relative z-10 mb-6">
                            <h3 className="font-display font-bold text-2xl sm:text-3xl tracking-tight leading-tight mb-2 text-white">
                                Know exactly what you can buy.
                            </h3>
                            <p className="text-white/65 text-sm leading-relaxed max-w-md">
                                Turn your rent into the real home price you can
                                target — with the down-payment plan to match.
                            </p>
                        </div>

                        {/* Sample affordability: rent → home conversion (rent-gated per product decision). */}
                        <div className="relative z-10 rounded-2xl bg-white/5 border border-white/10 p-4 md:p-5 backdrop-blur-sm">
                            <div className="grid grid-cols-12 items-center gap-3">
                                <div className="col-span-5">
                                    <div className="text-[9px] tracking-[0.25em] uppercase text-white/45 font-semibold mb-1">
                                        You pay
                                    </div>
                                    <div
                                        data-testid="affordability-card-rent"
                                        className="font-display font-extrabold text-2xl text-white tabular-nums leading-none"
                                    >
                                        {rentDisplay}
                                        {hasRent && (
                                            <span className="text-xs text-white/45 ml-1 font-semibold">
                                                /mo
                                            </span>
                                        )}
                                    </div>
                                </div>
                                <div className="col-span-2 flex justify-center">
                                    <ArrowRight
                                        className="w-5 h-5 text-[#1E6BFF]"
                                        strokeWidth={2.5}
                                    />
                                </div>
                                <div className="col-span-5 text-right">
                                    <div className="text-[9px] tracking-[0.25em] uppercase text-white/45 font-semibold mb-1">
                                        You could own
                                    </div>
                                    <div
                                        data-testid="affordability-card-home"
                                        className="font-display font-extrabold text-2xl text-[#7FA8FF] tabular-nums leading-none"
                                    >
                                        {homeDisplay}
                                    </div>
                                </div>
                            </div>

                            <div className="mt-5 pt-5 border-t border-white/10 text-center">
                                <div
                                    data-testid="affordability-card-emi"
                                    className="font-display font-extrabold text-lg text-white tabular-nums leading-none"
                                >
                                    {emiDisplay}
                                    {hasRent && (
                                        <span className="text-xs text-white/45 ml-1 font-semibold">
                                            /mo
                                        </span>
                                    )}
                                </div>
                                <div className="mt-1.5 text-[9px] tracking-[0.18em] uppercase text-white/45 font-semibold">
                                    Estimated EMI
                                </div>
                            </div>

                            {!hasRent && (
                                <div
                                    data-testid="affordability-card-empty-hint"
                                    className="mt-4 pt-4 border-t border-white/10 text-[10px] tracking-[0.22em] uppercase text-white/55 font-semibold text-center"
                                >
                                    Enter your rent above to see live numbers
                                </div>
                            )}

                            {hasRent && (
                                <p
                                    data-testid="affordability-card-caption"
                                    className="mt-4 pt-4 border-t border-white/10 text-[10px] text-white/45 leading-relaxed text-center"
                                >
                                    Estimated using a typical buyer profile.
                                    Personalise your numbers in the Affordability Assessment.
                                </p>
                            )}

                            {/* Sparkline — narrative decoration, not a personalised chart. */}
                            <div className="mt-5 flex items-end gap-1 h-10">
                                {[
                                    24, 18, 32, 28, 40, 36, 48, 42, 56, 50, 64,
                                    58, 70, 66, 76,
                                ].map((h, j) => (
                                    <div
                                        key={j}
                                        className="flex-1 rounded-sm bg-[#1E6BFF]"
                                        style={{
                                            height: `${h}%`,
                                            opacity: 0.4 + j * 0.04,
                                        }}
                                    />
                                ))}
                            </div>
                            <div className="mt-2 flex justify-between text-[9px] tracking-[0.18em] uppercase text-white/40 font-semibold">
                                <span>Today · renting</span>
                                <span>5 yrs · owning</span>
                            </div>
                        </div>
                    </motion.button>

                    {/* Compact intelligence cards — denser */}
                    {intelligences.map((f, i) => {
                        const Icon = f.icon;
                        const span =
                            i === 0
                                ? "md:col-span-4"
                                : i === 1
                                  ? "md:col-span-4"
                                  : i === 2
                                    ? "md:col-span-5"
                                    : "md:col-span-7";
                        return (
                            <motion.button
                                key={f.id}
                                type="button"
                                onClick={() => setActiveSection(f.id)}
                                data-testid={`intelligence-card-${f.id}`}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-80px" }}
                                transition={{
                                    duration: 0.7,
                                    delay: 0.05 + i * 0.05,
                                    ease: [0.16, 1, 0.3, 1],
                                }}
                                className={`${span} text-left group relative overflow-hidden rounded-3xl border bg-white border-[#0A1F44]/10 hover:border-[#1E6BFF]/40 hover:-translate-y-1 p-6 transition-all duration-500 flex flex-col cursor-pointer`}
                            >
                                <div className="flex items-start justify-between mb-4">
                                    <span className="text-[11px] tracking-[0.22em] uppercase font-semibold text-[#1E6BFF]">
                                        {`0${i + 2}`} — {f.label}
                                    </span>
                                    <Icon
                                        className="w-4 h-4 text-[#0A1F44] group-hover:text-[#1E6BFF] transition-colors"
                                        strokeWidth={1.75}
                                    />
                                </div>
                                <h3 className="font-display font-bold text-xl sm:text-2xl tracking-tight leading-tight mb-2 text-[#0A1F44]">
                                    {f.title}
                                </h3>
                                <p className="text-[#0A1F44]/60 text-sm leading-relaxed mb-4">
                                    {f.body}
                                </p>

                                <div className="mt-auto pt-3 border-t border-[#0A1F44]/8">
                                    <div className="font-display font-extrabold text-xl text-[#0A1F44] tabular-nums leading-none">
                                        {f.stat.v}
                                    </div>
                                    <div className="mt-1 text-[10px] tracking-[0.18em] uppercase text-[#0A1F44]/50 font-semibold">
                                        {f.stat.l}
                                    </div>
                                    {f.chips && (
                                        <div className="mt-3 flex flex-wrap gap-1.5">
                                            {f.chips.map((c) => (
                                                <span
                                                    key={c}
                                                    className="px-2.5 py-1 rounded-full bg-[#F7F8FB] border border-[#0A1F44]/8 text-[11px] font-semibold text-[#0A1F44]"
                                                >
                                                    {c}
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </motion.button>
                        );
                    })}
                </div>

                <div className="mt-12 md:mt-16 grid grid-cols-2 md:grid-cols-4 gap-6 border-t border-[#0A1F44]/10 pt-10">
                    {[
                        { v: "142", l: "market signals" },
                        { v: "2.4k", l: "metros covered" },
                        { v: "6h", l: "refresh cadence" },
                        { v: "98%", l: "valuation accuracy" },
                    ].map((s) => (
                        <div
                            key={s.l}
                            data-testid={`stat-${s.l.replace(/\s/g, "-")}`}
                        >
                            <div className="font-display font-extrabold text-4xl md:text-5xl tracking-tight text-[#0A1F44]">
                                {s.v}
                            </div>
                            <div className="mt-2 text-[11px] tracking-[0.25em] uppercase text-[#0A1F44]/55 font-medium">
                                {s.l}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}
